import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { usePartnerGetRefreshToken } from 'hooks/requests';
import { useAuthentication } from 'hooks/useAuthentication';
import moment from 'moment';
import { Jwt } from 'types/auth';

export interface CountdownProps {
  initialSeconds: number;
  onCountdownEnd: () => void;
}

export const Countdown: React.FC<CountdownProps> = ({
  initialSeconds = 0,
  onCountdownEnd
}) => {
  const [seconds, setSeconds] = useState(initialSeconds);
  const isCountdownFinished = seconds <= 0;

  useEffect(() => {
    if (isCountdownFinished) return;
    const countdown = setInterval(() => {
      setSeconds(prevState => prevState - 1);
    }, 1000);
    return () => {
      clearInterval(countdown);
    };
  }, [isCountdownFinished]);

  useEffect(() => {
    if (isCountdownFinished) {
      onCountdownEnd();
    }
  }, [isCountdownFinished, onCountdownEnd]);
  return <span>{String(seconds)}</span>;
};

function SessionTimeout() {
  const { data: refreshToken = '', refetch } = usePartnerGetRefreshToken();
  const { getToken, getDecodeToken, logoutNavigate } = useAuthentication();
  const token = getToken();
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (refreshToken) {
      sessionStorage.setItem('usertoken', refreshToken);
    }
  }, [refreshToken]);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!token) return; // Don't set warning timer if no token exists

    const warning = setTimeout(() => {
      setOpen(true);
    }, Math.floor(moment((getDecodeToken() as unknown as Jwt).warn).diff(moment())));

    return () => {
      clearTimeout(warning);
    };
  }, [refreshToken, getDecodeToken, token]);

  const logout = useCallback(() => {
    setOpen(false);
    logoutNavigate(navigate);
  }, [logoutNavigate, navigate]);

  useEffect(() => {
    // Only logout if token is missing and we're not in the middle of refreshing
    if (!token && !refreshing) {
      logout();
    }
  }, [token, logout, refreshing]);

  const resetTimeout = async () => {
    try {
      setRefreshing(true);
      await refetch();
      setOpen(false);
    } catch (error) {
      console.error('Failed to refresh token:', error);
      logout();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Dialog
      open={open}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        You will be logged out in{' '}
        {open ? (
          <Countdown
            initialSeconds={Number(
              Math.floor(
                moment((getDecodeToken() as unknown as Jwt).exp * 1000).diff(
                  moment()
                ) / 1000
              )
            )}
            onCountdownEnd={logout}
          />
        ) : null}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Your session is set to expire due to inactivity.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={logout} color="primary">
          Logout
        </Button>
        <Button onClick={resetTimeout} color="primary" autoFocus>
          Stay Logged In
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SessionTimeout;
